/**
 * Live screen-frame preview that scales to the entered Width × Height aspect ratio
 * and renders Tension Spring + Pull Tab markers on the correct edges.
 *
 * Springs go on the chosen springSide (top|bottom|left|right);
 * pull tabs are auto-rendered on the OPPOSITE single edge.
 *
 * Spring markers sit ~1.5" from each corner of the spring edge.
 * Pull-tab markers sit ~4.5" from each corner of the opposite edge.
 */

const FRAME_FILL = "#f5f7f6";
const FRAME_STROKE = "var(--brand-text)";
const ACCENT = "var(--brand-green)";

function formatInches(v) {
  if (v === "" || v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const total16 = Math.round(n * 16);
  const whole = Math.floor(total16 / 16);
  const sx = total16 % 16;
  if (sx === 0) return `${whole}"`;
  const g = (a, b) => (b ? g(b, a % b) : a);
  const d = g(sx, 16);
  return `${whole > 0 ? whole + " " : ""}${sx / d}/${16 / d}"`;
}

/** Spring zigzag along an edge. dir = 'horizontal' or 'vertical'. */
function SpringIcon({ cx, cy, dir, len = 18 }) {
  const half = len / 2;
  const ticks = 5;
  const step = len / (ticks - 1);
  const amp = 3.5;
  const pts = [];
  if (dir === "horizontal") {
    for (let i = 0; i < ticks; i++) {
      const x = cx - half + i * step;
      const y = cy + (i % 2 === 0 ? -amp : amp);
      pts.push(`${x},${y}`);
    }
  } else {
    for (let i = 0; i < ticks; i++) {
      const y = cy - half + i * step;
      const x = cx + (i % 2 === 0 ? -amp : amp);
      pts.push(`${x},${y}`);
    }
  }
  return (
    <polyline
      points={pts.join(" ")}
      fill="none"
      stroke={ACCENT}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

/** Small pull-tab rectangle protruding outward from an edge. */
function PullTabIcon({ cx, cy, side }) {
  const w = 12, h = 5, r = 1.5, gap = 1;
  const horiz = side === "top" || side === "bottom";
  let x, y, rectW, rectH;
  if (side === "top") {
    rectW = w; rectH = h;
    x = cx - w / 2; y = cy - h - gap;
  } else if (side === "bottom") {
    rectW = w; rectH = h;
    x = cx - w / 2; y = cy + gap;
  } else if (side === "left") {
    rectW = h; rectH = w;
    x = cx - h - gap; y = cy - w / 2;
  } else {
    rectW = h; rectH = w;
    x = cx + gap; y = cy - w / 2;
  }
  return <rect x={x} y={y} width={rectW} height={rectH} rx={r} ry={r} fill={ACCENT} opacity="0.9" />;
}

export default function ScreenVisualizer({
  width, height, springSide, hasSprings = true, hasPullTabs = true,
}) {
  const w = Math.max(0, Number(width) || 0);
  const h = Math.max(0, Number(height) || 0);

  const VB_W = 320, VB_H = 260;
  const PAD_TOP = 28, PAD_BOTTOM = 44, PAD_LEFT = 56, PAD_RIGHT = 28;
  const maxFrameW = VB_W - PAD_LEFT - PAD_RIGHT;
  const maxFrameH = VB_H - PAD_TOP - PAD_BOTTOM;
  const ratio = w > 0 && h > 0 ? w / h : 36 / 48;
  let fW = maxFrameW, fH = maxFrameW / ratio;
  if (fH > maxFrameH) {
    fH = maxFrameH;
    fW = maxFrameH * ratio;
  }
  const x0 = PAD_LEFT + (maxFrameW - fW) / 2;
  const y0 = PAD_TOP + (maxFrameH - fH) / 2;
  const x1 = x0 + fW, y1 = y0 + fH;
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const empty = !(w > 0 && h > 0);

  // 1 inch in SVG units (use the smaller of the two scales — they should be equal anyway).
  const pxPerInch = !empty ? Math.min(fW / w, fH / h) : 4;

  // Spring markers sit ~1.5" from each corner; pull-tab markers sit ~4.5" from each corner.
  // Floor (in SVG units) so they remain visible on tiny frames.
  const springInset = Math.max(6, 1.5 * pxPerInch);
  const tabInset = Math.max(16, 4.5 * pxPerInch);
  // Cap insets to avoid both markers overlapping past center on small frames.
  const clampInsetH = (i) => Math.min(i, fW / 2 - 4);
  const clampInsetV = (i) => Math.min(i, fH / 2 - 4);

  const showMarkers = !!springSide && springSide !== "unsure" && !empty;
  const opposite = { top: "bottom", bottom: "top", left: "right", right: "left" }[springSide];

  // Compute spring + tab positions for a given side.
  const positionsForSide = (side, inset, isHoriz) => {
    if (side === "top") {
      const i = clampInsetH(inset);
      return [
        { cx: x0 + i, cy: y0, dir: "horizontal", side: "top" },
        { cx: x1 - i, cy: y0, dir: "horizontal", side: "top" },
      ];
    }
    if (side === "bottom") {
      const i = clampInsetH(inset);
      return [
        { cx: x0 + i, cy: y1, dir: "horizontal", side: "bottom" },
        { cx: x1 - i, cy: y1, dir: "horizontal", side: "bottom" },
      ];
    }
    if (side === "left") {
      const i = clampInsetV(inset);
      return [
        { cx: x0, cy: y0 + i, dir: "vertical", side: "left" },
        { cx: x0, cy: y1 - i, dir: "vertical", side: "left" },
      ];
    }
    // right
    const i = clampInsetV(inset);
    return [
      { cx: x1, cy: y0 + i, dir: "vertical", side: "right" },
      { cx: x1, cy: y1 - i, dir: "vertical", side: "right" },
    ];
    // eslint-disable-next-line no-unused-vars
    void isHoriz;
  };

  const springSpots = showMarkers ? positionsForSide(springSide, springInset) : [];
  const tabSpots = showMarkers ? positionsForSide(opposite, tabInset) : [];

  return (
    <figure
      className="rounded-2xl border border-[var(--brand-border)] p-3 mb-3"
      style={{ background: "#fff" }}
      data-testid="screen-visualizer"
    >
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" role="img" aria-label="Screen frame preview">
        <defs>
          <pattern id="meshHatch" width="6" height="6" patternUnits="userSpaceOnUse">
            <path d="M 0 6 L 6 0" stroke="#cbd5d0" strokeWidth="0.6" />
          </pattern>
        </defs>

        {/* drop shadow */}
        <rect x={x0 - 3} y={y0 - 3} width={fW + 6} height={fH + 6} rx="6" ry="6" fill="#000" opacity="0.04" />

        {/* frame */}
        <rect
          x={x0} y={y0} width={fW} height={fH}
          rx="4" ry="4"
          fill={empty ? "#fafafa" : FRAME_FILL}
          stroke={empty ? "#d1d5db" : FRAME_STROKE}
          strokeWidth="2.5"
          strokeDasharray={empty ? "4 4" : undefined}
          data-testid="screen-frame"
        />
        {!empty && (
          <rect
            x={x0 + 8} y={y0 + 8}
            width={Math.max(0, fW - 16)}
            height={Math.max(0, fH - 16)}
            fill="url(#meshHatch)"
          />
        )}

        {/* Width dimension callout */}
        <line x1={x0} y1={y1 + 16} x2={x1} y2={y1 + 16} stroke="#9ca3af" strokeWidth="1" />
        <line x1={x0} y1={y1 + 12} x2={x0} y2={y1 + 20} stroke="#9ca3af" strokeWidth="1" />
        <line x1={x1} y1={y1 + 12} x2={x1} y2={y1 + 20} stroke="#9ca3af" strokeWidth="1" />
        <text
          x={cx} y={y1 + 34}
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="var(--brand-text)"
          data-testid="visual-width-label"
        >
          {`W: ${formatInches(w)}`}
        </text>

        {/* Height dimension callout (rotated label on left) */}
        <line x1={x0 - 16} y1={y0} x2={x0 - 16} y2={y1} stroke="#9ca3af" strokeWidth="1" />
        <line x1={x0 - 12} y1={y0} x2={x0 - 20} y2={y0} stroke="#9ca3af" strokeWidth="1" />
        <line x1={x0 - 12} y1={y1} x2={x0 - 20} y2={y1} stroke="#9ca3af" strokeWidth="1" />
        <text
          x={x0 - 34} y={cy}
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="var(--brand-text)"
          transform={`rotate(-90, ${x0 - 34}, ${cy})`}
          data-testid="visual-height-label"
        >
          {`H: ${formatInches(h)}`}
        </text>

        {/* Springs (chosen edge) */}
        {hasSprings && springSpots.length > 0 && (
          <g data-testid="visual-springs">
            {springSpots.map((s, i) => (
              <SpringIcon
                key={i}
                cx={s.cx}
                cy={s.cy}
                dir={s.dir}
                len={s.dir === "horizontal" ? Math.min(22, fW * 0.18) : Math.min(22, fH * 0.18)}
              />
            ))}
          </g>
        )}

        {/* Pull tabs (opposite edge) */}
        {hasPullTabs && tabSpots.length > 0 && (
          <g data-testid="visual-pull-tabs">
            {tabSpots.map((t, i) => (
              <PullTabIcon key={i} cx={t.cx} cy={t.cy} side={t.side} />
            ))}
          </g>
        )}
      </svg>

      <figcaption className="flex justify-center gap-4 text-[11px] mt-1">
        {hasSprings && showMarkers && (
          <span className="inline-flex items-center gap-1 brand-text font-semibold">
            <svg width="14" height="10"><polyline points="0,5 3,1 6,9 9,1 12,9" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
            Springs
          </span>
        )}
        {hasPullTabs && showMarkers && (
          <span className="inline-flex items-center gap-1 brand-text font-semibold">
            <svg width="14" height="10"><rect x="1" y="2" width="12" height="6" rx="1.5" fill="currentColor" /></svg>
            Pull tabs
          </span>
        )}
        {!showMarkers && (
          <span className="muted-text italic">
            Pick the spring edge below to see springs &amp; pull tabs.
          </span>
        )}
      </figcaption>
    </figure>
  );
}
